import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH } from './gameConfig';
import { GameMode } from '../types/GameMode';
import { AILevel, AIConfig } from '../ai/types';
import { MONSTER_DATABASE } from '../constants/monsters';
import { PartyMemberState } from '../battle/partyBattle';
import {
  CPU_DIFFICULTY_OPTIONS,
  DIFFICULTY_SELECT_LAYOUT,
  DIFFICULTY_SELECT_LABELS,
} from './difficultySelectConfig';
import { AudioKey } from '../constants/audioKeys';
import { playSe } from '../utils/audioManager';

/** DifficultySelectSceneに渡されるデータ */
export interface DifficultySelectSceneData {
  mode?: GameMode;
  playerMonsterId?: string;
  enemyMonsterId?: string | null;
  // 観戦モード用
  cpu1MonsterId?: string;
  cpu2MonsterId?: string | null;
  step?: 'cpu1' | 'cpu2';
  cpu1AiLevel?: AILevel;
  // カスタムAI用
  playerAIConfig?: AIConfig;
  cpu1AIConfig?: AIConfig;
  cpu2AIConfig?: AIConfig;
  // 3vs3用
  playerParty?: string[];
  enemyParty?: string[];
  // BGM自由選択
  selectedBgmKey?: string;
  // バトルマップ選択
  selectedBattleBg?: string;
}

/**
 * CPU難易度選択画面シーン
 *
 * 4段階の難易度ボタンと戻るボタンを表示する。
 * 観戦モードではCPU1/CPU2の難易度を順に選択する。
 */
export class DifficultySelectScene extends BaseScene {
  constructor() {
    super(SceneKey.DIFFICULTY_SELECT);
  }

  create(data?: DifficultySelectSceneData): void {
    const mode = data?.mode ?? GameMode.FREE_CPU;

    if (mode === GameMode.SPECTATOR) {
      this.createSpectatorMode(data);
    } else if (mode === GameMode.FREE_CPU_3V3) {
      this.create3v3Mode(data);
    } else {
      this.createNormalMode(data);
    }
  }

  private createNormalMode(data?: DifficultySelectSceneData): void {
    const mode = data?.mode ?? GameMode.FREE_CPU;
    const playerMonsterId = data?.playerMonsterId ?? '';
    const enemyMonsterId = data?.enemyMonsterId ?? null;

    this.createTitle(DIFFICULTY_SELECT_LABELS.title);
    this.createDifficultyButtons((aiLevel) => {
      const resolvedEnemyId = enemyMonsterId ?? this.selectRandomEnemy(playerMonsterId);
      this.transitionTo(SceneKey.BATTLE, {
        monsterId: playerMonsterId,
        enemyMonsterId: resolvedEnemyId,
        aiLevel,
        mode,
        playerAIConfig: data?.playerAIConfig,
        selectedBgmKey: data?.selectedBgmKey,
        selectedBattleBg: data?.selectedBattleBg,
      });
    });
    this.createBackButton(() => {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode,
        step: 'opponent',
        playerMonsterId,
        playerAIConfig: data?.playerAIConfig,
        selectedBgmKey: data?.selectedBgmKey,
        selectedBattleBg: data?.selectedBattleBg,
      });
    });
  }

  private createSpectatorMode(data?: DifficultySelectSceneData): void {
    const step = data?.step ?? 'cpu1';
    const cpu1MonsterId = data?.cpu1MonsterId ?? '';
    const cpu2MonsterId = data?.cpu2MonsterId ?? null;
    const cpu1AiLevel = data?.cpu1AiLevel;

    if (step === 'cpu1') {
      this.createTitle('CPU1 難易度選択');
      this.createDifficultyButtons((aiLevel) => {
        this.transitionTo(SceneKey.DIFFICULTY_SELECT, {
          mode: GameMode.SPECTATOR,
          step: 'cpu2',
          cpu1MonsterId,
          cpu2MonsterId,
          cpu1AiLevel: aiLevel,
          cpu1AIConfig: data?.cpu1AIConfig,
          cpu2AIConfig: data?.cpu2AIConfig,
          selectedBgmKey: data?.selectedBgmKey,
          selectedBattleBg: data?.selectedBattleBg,
        });
      });
      this.createBackButton(() => {
        this.transitionTo(SceneKey.CHARACTER_SELECT, {
          mode: GameMode.SPECTATOR,
          step: 'cpu2',
          cpu1MonsterId,
          cpu1AIConfig: data?.cpu1AIConfig,
          selectedBgmKey: data?.selectedBgmKey,
          selectedBattleBg: data?.selectedBattleBg,
        });
      });
    } else {
      // step === 'cpu2'
      this.createTitle('CPU2 難易度選択');
      this.createDifficultyButtons((aiLevel) => {
        const resolvedCpu2Id = cpu2MonsterId ?? this.selectRandomEnemy(cpu1MonsterId);
        this.transitionTo(SceneKey.BATTLE, {
          mode: GameMode.SPECTATOR,
          monsterId: cpu1MonsterId,
          enemyMonsterId: resolvedCpu2Id,
          aiLevel: cpu1AiLevel,
          enemyAiLevel: aiLevel,
          playerAIConfig: data?.cpu1AIConfig,
          enemyAIConfig: data?.cpu2AIConfig,
          selectedBgmKey: data?.selectedBgmKey,
          selectedBattleBg: data?.selectedBattleBg,
        });
      });
      this.createBackButton(() => {
        this.transitionTo(SceneKey.DIFFICULTY_SELECT, {
          mode: GameMode.SPECTATOR,
          step: 'cpu1',
          cpu1MonsterId,
          cpu2MonsterId,
          cpu1AIConfig: data?.cpu1AIConfig,
          cpu2AIConfig: data?.cpu2AIConfig,
          selectedBgmKey: data?.selectedBgmKey,
          selectedBattleBg: data?.selectedBattleBg,
        });
      });
    }
  }

  private createTitle(titleText: string): void {
    this.add
      .text(GAME_WIDTH / 2, DIFFICULTY_SELECT_LAYOUT.titleY, titleText, {
        fontSize: '40px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private createDifficultyButtons(onSelect: (aiLevel: AILevel) => void): void {
    CPU_DIFFICULTY_OPTIONS.forEach((option, index) => {
      const y = DIFFICULTY_SELECT_LAYOUT.buttonStartY + index * DIFFICULTY_SELECT_LAYOUT.buttonSpacing;

      const text = this.add
        .text(GAME_WIDTH / 2, y, option.label, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => {
        text.setColor('#ffffff');
        text.setScale(1.1);
      });

      text.on('pointerout', () => {
        text.setColor('#ffffff');
        text.setScale(1.0);
      });

      text.on('pointerdown', () => {
        playSe(this.sound, AudioKey.SE_SELECT);
        onSelect(option.aiLevel);
      });
    });
  }

  private createBackButton(onBack: () => void): void {
    const text = this.add
      .text(GAME_WIDTH / 2, DIFFICULTY_SELECT_LAYOUT.backButtonY, DIFFICULTY_SELECT_LABELS.back, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    text.on('pointerover', () => {
      text.setColor('#ffffff');
      text.setScale(1.1);
    });

    text.on('pointerout', () => {
      text.setColor('#ffffff');
      text.setScale(1.0);
    });

    text.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      onBack();
    });
  }

  private create3v3Mode(data?: DifficultySelectSceneData): void {
    const playerParty = data?.playerParty ?? [];
    const enemyParty = data?.enemyParty ?? [];

    this.createTitle('3vs3 難易度選択');
    this.createDifficultyButtons((aiLevel) => {
      const resolvedEnemyParty = enemyParty;
      this.transitionTo(SceneKey.BATTLE, {
        mode: GameMode.FREE_CPU_3V3,
        aiLevel,
        playerParty: playerParty.map((monsterId) => ({
          monsterId,
          isDefeated: false,
          remainingSpecialCount: null,
          usedReflectCount: 0,
        })),
        enemyParty: resolvedEnemyParty.map((monsterId) => ({
          monsterId,
          isDefeated: false,
          remainingSpecialCount: null,
          usedReflectCount: 0,
        })),
        playerPartyIndex: 0,
        enemyPartyIndex: 0,
        selectedBgmKey: data?.selectedBgmKey,
        selectedBattleBg: data?.selectedBattleBg,
      });
    });
    this.createBackButton(() => {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: GameMode.FREE_CPU_3V3,
        step: 'opponent3',
        playerParty,
        enemyParty: enemyParty.slice(0, 2),
      });
    });
  }

  private selectRandomEnemy(excludeId: string): string {
    const candidates = MONSTER_DATABASE.filter((m) => m.id !== excludeId);
    const index = Math.floor(Math.random() * candidates.length);
    return candidates[index].id;
  }
}
